'use strict';

import {getLabcodeStudentToken} from './labcode-student-session';

/**
 * URL base da API Labcode (sem barra final).
 * @returns {string|null}
 */
export function getLabcodeApiBaseUrl () {
    const raw = typeof process !== 'undefined' && process.env && process.env.REACT_APP_LABCODE_API_URL;
    if (!raw || typeof raw !== 'string') return null;
    const s = raw.replace(/\/$/, '');
    return s.length ? s : null;
}

/**
 * @param {string} classCode
 * @param {string} nickname
 * @returns {Promise<{ token: string, student: object }>}
 */
export async function studentLogin (classCode, nickname) {
    const base = getLabcodeApiBaseUrl();
    if (!base) {
        throw new Error('API Labcode não configurada (REACT_APP_LABCODE_API_URL).');
    }
    let res;
    try {
        res = await fetch(`${base}/auth/student-login`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                classCode: String(classCode || '').trim(),
                nickname: String(nickname || '').trim()
            })
        });
    } catch (err) {
        const msg = err && err.message ? String(err.message) : '';
        if (/Failed to fetch|NetworkError|network/i.test(msg) || err.name === 'TypeError') {
            throw new Error(
                'Não foi possível contactar a API. Arranque o labcode-server (ex.: porta 3000), confirme REACT_APP_LABCODE_API_URL no .env do frontend e volte a fazer npm start.'
            );
        }
        throw err;
    }
    let data = {};
    const text = await res.text();
    const trimmed = text && text.trim ? text.trim() : '';
    if (trimmed.length) {
        try {
            data = JSON.parse(trimmed);
        } catch (parseErr) {
            data = { error: trimmed.slice(0, 200) };
        }
    }
    if (!res.ok) {
        throw new Error(
            data.error ||
                (res.status >= 500
                    ? `Erro no servidor (${res.status}). Confirme que labcode-server está a correr e veja o terminal do servidor.`
                    : `Erro ao entrar (${res.status}).`)
        );
    }
    if (!data.token || !data.student) {
        throw new Error('Resposta inválida do servidor.');
    }
    return {token: data.token, student: data.student};
}

/**
 * @param {string} email
 * @param {string} password
 * @returns {Promise<{ token: string, staff: object }>}
 */
export async function staffLogin (email, password) {
    const base = getLabcodeApiBaseUrl();
    if (!base) {
        throw new Error('API Labcode não configurada (REACT_APP_LABCODE_API_URL).');
    }
    let res;
    try {
        res = await fetch(`${base}/auth/staff-login`, {
            method: 'POST',
            headers: {'Content-Type': 'application/json'},
            body: JSON.stringify({
                email: String(email || '').trim(),
                password: String(password || '')
            })
        });
    } catch (err) {
        const msg = err && err.message ? String(err.message) : '';
        if (/Failed to fetch|NetworkError|network/i.test(msg) || err.name === 'TypeError') {
            throw new Error(
                'Não foi possível contactar a API. Arranque o labcode-server (ex.: porta 3000), confirme REACT_APP_LABCODE_API_URL no .env do frontend e volte a fazer npm start.'
            );
        }
        throw err;
    }
    let data = {};
    const text = await res.text();
    const trimmed = text && text.trim ? text.trim() : '';
    if (trimmed.length) {
        try {
            data = JSON.parse(trimmed);
        } catch (parseErr) {
            data = { error: trimmed.slice(0, 200) };
        }
    }
    if (!res.ok) {
        throw new Error(
            data.error ||
                (res.status >= 500
                    ? `Erro no servidor (${res.status}). Confirme que labcode-server está a correr e veja o terminal do servidor.`
                    : `Erro ao entrar (${res.status}).`)
        );
    }
    if (!data.token || !data.staff) {
        throw new Error('Resposta inválida do servidor.');
    }
    return {token: data.token, staff: data.staff};
}

/**
 * @returns {Promise<{ projects: Array<object> }>}
 */
export async function listLabcodeStudentProjects () {
    const base = getLabcodeApiBaseUrl();
    const token = getLabcodeStudentToken();
    if (!base || !token) {
        throw new Error('Sessão de aluno ou API não disponível.');
    }
    const res = await fetch(`${base}/projects/list`, {
        headers: {Authorization: `Bearer ${token}`}
    });
    let data = {};
    const text = await res.text();
    const trimmed = text && text.trim ? text.trim() : '';
    if (trimmed.length) {
        try {
            data = JSON.parse(trimmed);
        } catch (parseErr) {
            data = { error: trimmed.slice(0, 200) };
        }
    }
    if (!res.ok) {
        throw new Error(
            data.error ||
                (res.status >= 500 ? `Erro no servidor (${res.status}).` : `Pedido falhou (${res.status}).`)
        );
    }
    if (!data.projects) {
        throw new Error('Resposta inválida do servidor.');
    }
    return data;
}

/**
 * @param {{ title: string, blockData: object, projectId?: string|null }} body
 * @returns {Promise<object>}
 */
export async function saveLabcodeStudentProject (body) {
    const base = getLabcodeApiBaseUrl();
    const token = getLabcodeStudentToken();
    if (!base || !token) {
        throw new Error('Sessão de aluno ou API não disponível.');
    }
    const payload = {
        title: body.title,
        blockData: body.blockData
    };
    if (body.projectId) {
        payload.projectId = body.projectId;
    }
    const res = await fetch(`${base}/projects/save`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
    });
    let data = {};
    const text = await res.text();
    const trimmed = text && text.trim ? text.trim() : '';
    if (trimmed.length) {
        try {
            data = JSON.parse(trimmed);
        } catch (parseErr) {
            data = { error: trimmed.slice(0, 200) };
        }
    }
    if (!res.ok) {
        throw new Error(
            data.error ||
                (res.status >= 500 ? `Erro no servidor (${res.status}).` : `Erro ao guardar (${res.status}).`)
        );
    }
    return data;
}

/**
 * @param {string} projectId
 * @returns {Promise<object>}
 */
export async function fetchLabcodeStudentProject (projectId) {
    const base = getLabcodeApiBaseUrl();
    const token = getLabcodeStudentToken();
    if (!base || !token) {
        throw new Error('Sessão de aluno ou API não disponível.');
    }
    const res = await fetch(`${base}/projects/load/${encodeURIComponent(projectId)}`, {
        headers: {Authorization: `Bearer ${token}`}
    });
    let data = {};
    const text = await res.text();
    const trimmed = text && text.trim ? text.trim() : '';
    if (trimmed.length) {
        try {
            data = JSON.parse(trimmed);
        } catch (parseErr) {
            data = { error: trimmed.slice(0, 200) };
        }
    }
    if (!res.ok) {
        throw new Error(
            data.error ||
                (res.status >= 500 ? `Erro no servidor (${res.status}).` : `Erro ao carregar (${res.status}).`)
        );
    }
    return data;
}

/**
 * @param {Blob} blob
 * @returns {Promise<string>}
 */
export function blobToBase64 (blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
            const s = reader.result;
            if (typeof s !== 'string') {
                reject(new Error('Falha ao codificar o projeto.'));
                return;
            }
            const i = s.indexOf(',');
            resolve(i >= 0 ? s.slice(i + 1) : s);
        };
        reader.onerror = () => reject(new Error('Falha ao ler o projeto.'));
        reader.readAsDataURL(blob);
    });
}

/**
 * @param {string} b64
 * @returns {ArrayBuffer}
 */
export function base64ToArrayBuffer (b64) {
    const bin = atob(b64);
    const len = bin.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
        bytes[i] = bin.charCodeAt(i);
    }
    return bytes.buffer;
}
